import React from 'react';
import { Plus, Trash2, Image, GripVertical } from 'lucide-react';
import ColorEditor from '../ColorEditor';

const TestimonialsEditor = ({ content, onContentChange, onArrayFieldChange, onAddArrayItem, onRemoveArrayItem, uploading, onImageUpload, onOpenImagePicker, site, onCreateCustomTheme }) => {
  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full text-xs px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="What Our Clients Say"
        />
      </div>

      {/* Testimonial Elements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Testimonial elements
        </label>
        <div className="space-y-2">
          {/* Section Heading Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Section heading</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="testimonialsHeadingToggle"
                type="checkbox" 
                checked={content.showHeading !== false}
                onChange={(e) => onContentChange('showHeading', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="testimonialsHeadingToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Avatars Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Avatars</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="testimonialsAvatarsToggle"
                type="checkbox" 
                checked={content.showAvatars !== false}
                onChange={(e) => onContentChange('showAvatars', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="testimonialsAvatarsToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Ratings Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Star ratings</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="testimonialsRatingsToggle"
                type="checkbox" 
                checked={content.showRatings !== false}
                onChange={(e) => onContentChange('showRatings', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="testimonialsRatingsToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Company Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Company</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="testimonialsCompanyToggle"
                type="checkbox" 
                checked={content.showCompany !== false}
                onChange={(e) => onContentChange('showCompany', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="testimonialsCompanyToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials List */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Testimonials</label>
        <div className="space-y-4">
          {(content.testimonials || []).map((testimonial, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <GripVertical className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Testimonial {index + 1}</span>
                </div>
                <button
                  onClick={() => onRemoveArrayItem('testimonials', index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              {/* Avatar */}
              {content.showAvatars !== false && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Avatar</label>
                  <div className="flex flex-col items-center space-y-2">
                    <div 
                      className="w-16 h-16 bg-gray-100 border-2 border-dashed border-gray-300 rounded-full flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                      onClick={() => document.getElementById(`testimonialAvatarInput${index}`).click()}
                    >
                      {testimonial.avatar ? (
                        <img 
                          src={testimonial.avatar} 
                          alt={testimonial.name || `Person ${index + 1}`}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <div className="text-center">
                          <Image className="w-4 h-4 text-gray-400 mx-auto" />
                        </div>
                      )}
                      <input
                        id={`testimonialAvatarInput${index}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => onImageUpload(e.target.files[0], `testimonials.${index}.avatar`, testimonial.name || `testimonial-${index}`)}
                        className="hidden"
                        disabled={uploading}
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={() => document.getElementById(`testimonialAvatarInput${index}`).click()}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                        disabled={uploading}
                      >
                        Upload
                      </button>
                      {onOpenImagePicker && (
                        <button
                          type="button"
                          onClick={() => onOpenImagePicker(`testimonials.${index}.avatar`, testimonial.name || `testimonial-${index}`)}
                          className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          disabled={uploading}
                        >
                          Browse
                        </button>
                      )}
                    </div>
                    
                    {/* Photo Credit for Avatar */}
                    {testimonial.avatarCredit && (
                      <div className="text-xs text-gray-500 mt-1 text-center">
                        Photo by{' '}
                        <a 
                          href={testimonial.avatarCredit.photographerUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          {testimonial.avatarCredit.photographer}
                        </a>
                        {' '}on Unsplash
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Testimonial Text */}
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Testimonial</label>
                <textarea
                  rows={5}
                  value={testimonial.text || ''}
                  onChange={(e) => onArrayFieldChange('testimonials', index, { ...testimonial, text: e.target.value })}
                  className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Amazing service! They exceeded our expectations in every way..."
                />
              </div>
              
              {/* Name and Title */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={testimonial.name || ''}
                    onChange={(e) => onArrayFieldChange('testimonials', index, { ...testimonial, name: e.target.value })}
                    className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={testimonial.title || ''}
                    onChange={(e) => onArrayFieldChange('testimonials', index, { ...testimonial, title: e.target.value })}
                    className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="CEO"
                  />
                </div>
              </div>
              
              {/* Company */}
              {content.showCompany !== false && (
                <div className="mb-3">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={testimonial.company || ''}
                    onChange={(e) => onArrayFieldChange('testimonials', index, { ...testimonial, company: e.target.value })}
                    className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ABC Corporation"
                  />
                </div>
              )}
              
              {/* Rating */}
              {content.showRatings !== false && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rating</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => onArrayFieldChange('testimonials', index, { ...testimonial, rating: star })}
                        className={`text-lg ${
                          (testimonial.rating || 5) >= star ? 'text-yellow-400' : 'text-gray-300'
                        }`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          
          <button
            onClick={() => onAddArrayItem('testimonials', { 
              name: '', 
              text: '', 
              rating: 5, 
              company: '', 
              title: '', 
              avatar: '' 
            })}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add testimonial
          </button>
        </div>
      </div>

      {/* Layout Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Layout & Style</label>
        <div className="space-y-2">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Columns</label>
            <select
              value={content.columns || '3'}
              onChange={(e) => onContentChange('columns', e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="1">1 Column</option>
              <option value="2">2 Columns</option>
              <option value="3">3 Columns</option>
              <option value="4">4 Columns</option>
              <option value="auto">Auto</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Text Alignment</label>
            <select
              value={content.textAlign || 'center'}
              onChange={(e) => onContentChange('textAlign', e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Card Style</label>
            <select
              value={content.cardStyle || 'default'}
              onChange={(e) => onContentChange('cardStyle', e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="default">Default</option>
              <option value="bordered">Bordered</option>
              <option value="shadow">Shadow</option>
              <option value="minimal">Minimal</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Avatar Style</label>
            <select
              value={content.avatarStyle || 'circle'}
              onChange={(e) => onContentChange('avatarStyle', e.target.value)}
              className="w-full text-xs px-2 py-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="circle">Circle</option>
              <option value="square">Square</option>
              <option value="rounded">Rounded Square</option>
            </select>
          </div>
        </div>
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default TestimonialsEditor;